/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import CustomerModel from 'cio/models/customer';
import { Task } from 'ember-concurrency';
import { Filters, isEmptyFiltersQuery } from 'cio/utils/filters';
import { FlyMouseEvent } from '@customerio/fly-components/types/events';
import rangeSelectionHelper from 'cio/utils/range-selection-helper';
import { queryEncode, safeQueryDecode } from 'cio/helpers/query';
import { SerializedLegacyFilter } from 'cio/types/conditions/legacy-filter';
import EnvironmentService from 'cio/services/environment';
import ObjectTypesService from 'cio/services/object-types';

interface Args {
  selectedPeople: CustomerModel[];
  setSelectedPeople: (c: CustomerModel[]) => unknown;
  loadPeopleTask: Task<CustomerModel[], []>;
  totalPeopleRecs: number;
  peoplePerPage: number;
  isLoading: boolean;
  peopleFilters: { email: string; filters: string; page: number };
  setPeopleFilters: (f: { email?: string; filters?: string; page?: number }) => unknown;
}

enum ActiveSearchBy {
  Email = 'email',
  Filter = 'filter',
}

export default class PeopleListComponent extends Component<Args> {
  @service environment!: EnvironmentService;
  @service objectTypes!: ObjectTypesService;

  @tracked activeSearchBy: ActiveSearchBy =
    this.args.peopleFilters.filters.length > 0 ? ActiveSearchBy.Filter : ActiveSearchBy.Email;
  @tracked allSelected = false;
  @tracked shiftSelectStart: number | null = null;
  @tracked shiftSelectEnd: number | null = null;

  activeSearchByValues = ActiveSearchBy;
  supportedConditionTypes = [
    'segments_negatable',
    'attribute',
    'device_attribute',
    'newsletter_recipients',
    'message',
    'event',
  ];

  get people() {
    return this.args.loadPeopleTask.lastSuccessful?.value ?? [];
  }

  get conditionTypes() {
    let conditionTypes = this.supportedConditionTypes;
    if (!this.environment.enabledAction('push')) {
      conditionTypes = conditionTypes.filter((t) => t !== 'device_attribute');
    }
    return conditionTypes;
  }

  get filterConditions() {
    return safeQueryDecode(this.args.peopleFilters.filters ?? '');
  }

  get allPeopleOnPageSelected() {
    if (this.args.selectedPeople.length === 0 || this.people?.length === 0) {
      return false;
    }

    if (this.allSelected && this.args.totalPeopleRecs > this.args.selectedPeople.length) {
      return false;
    }

    return this.args.selectedPeople.length === this.people?.length;
  }

  get allPeopleOnPageIndeterminate() {
    if (this.allSelected && this.args.totalPeopleRecs > this.args.selectedPeople.length) {
      return false;
    }

    return (this.args.selectedPeople?.length ?? 0) > 0 && !this.allPeopleOnPageSelected;
  }

  get hasFilters() {
    let { email, filters } = this.args.peopleFilters;
    return filters.length > 0 || email.length > 0;
  }

  @action
  updateFilters(conditions: SerializedLegacyFilter[] | Filters) {
    let filterString = queryEncode(conditions);

    // Reset the query param to nothing
    if (isEmptyFiltersQuery(filterString)) {
      this.clearFilters();
    } else {
      this.args.setPeopleFilters({ filters: filterString });
    }
    this.args.setPeopleFilters({ page: 1 });
    this.refreshPeopleList();
  }

  @action
  clearFilters() {
    this.args.setPeopleFilters({ email: '', filters: '' });
    this.clearSelection();
    this.refreshPeopleList();
  }

  @action
  activateSearchBy(activeSearchBy: ActiveSearchBy) {
    if (this.activeSearchBy !== activeSearchBy) {
      this.clearFilters();
      this.clearSelection();
      this.activeSearchBy = activeSearchBy;
    }
  }

  @action
  search(term: string) {
    this.args.setPeopleFilters({ email: term, page: 1 });
    this.clearSelection();
    this.refreshPeopleList();
  }

  @action
  clearSearch() {
    this.args.setPeopleFilters({ email: '' });
    this.clearSelection();
    this.refreshPeopleList();
  }

  @action
  selectAllPeopleOnPage(checked: boolean) {
    if (checked) {
      this.args.setSelectedPeople(this.people.slice());
      this.allSelected = this.people.length === this.args.totalPeopleRecs;
    } else {
      this.clearSelection();
    }
  }

  @action
  clearSelection() {
    this.args.setSelectedPeople([]);
    this.allSelected = false;
  }

  @action
  onPageChange(page: number) {
    this.args.setPeopleFilters({ page });
    this.allSelected = false;
    this.args.setSelectedPeople([]);
    this.refreshPeopleList();
  }

  @action
  personChecked(
    person: CustomerModel,
    index: number,
    checked: boolean,
    metadata: { event: FlyMouseEvent<HTMLInputElement> },
  ) {
    let tempSelectedPeople = this.args.selectedPeople.slice();

    let { tempShiftSelectEnd, tempShiftSelectStart, allSelected } = rangeSelectionHelper<CustomerModel>({
      models: this.people,
      singleModel: person,
      selectedItems: tempSelectedPeople,
      index,
      checked,
      totalRecs: this.args.totalPeopleRecs,
      metadata,
      shiftSelectStart: this.shiftSelectStart,
      shiftSelectEnd: this.shiftSelectEnd,
    });

    this.shiftSelectStart = tempShiftSelectStart;
    this.shiftSelectEnd = tempShiftSelectEnd;
    this.allSelected = allSelected;
    this.args.setSelectedPeople(tempSelectedPeople);
  }

  private refreshPeopleList() {
    this.args.loadPeopleTask.perform();
  }
}
